import React from "react";
import DatePickerInput from "../../components/form/DatePickerInput";

/**
 * `renderInputField`
 * ------------------
 * Pure factory that returns the correct `<input>` / `<select>` element
 * for a questionnaire *question* definition.
 *
 * @param {Object}          params
 * @param {Object}          params.question     Full question object
 * @param {*}               params.value        Current answer for this question
 * @param {Function}        params.onChange     Shared `onChange` handler
 * @param {Function}        [params.onEnter]    Called when user presses Enter
 * @param {React.RefObject} params.inputRef     Ref so parent can focus the field
 *
 * @returns {JSX.Element}  A ready-to-render React element
 *
 * Supported `question.fieldType` values:
 *   • "text"   – default fallback
 *   • "number"
 *   • "date"
 *   • "select" – requires `question.options = [{ value, label }]`
 *   • "range"  – requires `question.min`, `question.max`, optional `step`
 *
 *  This helper is **pure**: no React hooks, no side-effects.
 *  Safe to unit-test in isolation or move to a util module.
 */
export default function renderInputField({
  question,
  value,
  onChange,
  onEnter,
  inputRef,
  t = (k) => k, // injected translator (defaults to identity)
}) {
  const title = t(question.titleKey ?? question.title);
  const description = question.descriptionKey
    ? t(question.descriptionKey)
    : question.description;
  const rangeDesc = question.rangeDescriptionKey
    ? t(question.rangeDescriptionKey)
    : (question.rangeDescription ?? description);

  const helperText = question.helperTextKey ? t(question.helperTextKey) : null;
  const descId = helperText ? `${question.id}__hint` : undefined;

  // Base props shared by every field type -----------------------------
  // These ensure consistent styling, Enter-key behaviour, and ref wiring.
  const common = {
    id: question.id,
    name: question.id,
    placeholder: title,
    className: "custom-input w-full appearance-none",
    value: value ?? "",
    onChange,
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onEnter?.(e);
      }
    },
    required: question.required,
    ref: inputRef,
  };

  switch (question.fieldType) {
    case "number":
      // ----- number -----
      return <input type="number" {...common} />;
    case "date": {
      return (
        <DatePickerInput
          id={question.id}
          value={value}
          onChange={onChange}
          required={question.required}
          className=""
        />
      );
    }
    case "select":
      // ----- select -----
      return (
        <select {...common}>
          <option value="">{title}</option>
          {question.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey ?? opt.label)}
            </option>
          ))}
        </select>
      );
    case "range":
      // ----- range -----
      return (
        <div className="range-input-container">
          <input
            type="range"
            {...common}
            className="custom-range w-full"
            value={value ?? question.min}
            min={question.min}
            max={question.max}
            step={question.step || 1}
          />
          <div className="range-values text-xs flex justify-between">
            <span>{question.min}</span>
            <span className="subtitle-text">{value ?? question.min}</span>
            <span>{question.max}</span>
          </div>
          {rangeDesc && <p className="muted-text">{rangeDesc}</p>}
        </div>
      );

    case "radio":
      return (
        <fieldset className="space-y-2" id={question.id}>
          {helperText && (
            <p id={descId} className="muted-text">
              {helperText}
            </p>
          )}

          {question.options.map((opt) => {
            const val = String(opt.value);
            const checked = String(value ?? "") === val;

            return (
              <label key={val} className="radio-outlined-button">
                <input
                  type="radio"
                  name={question.id}
                  value={val}
                  checked={checked}
                  onChange={() =>
                    onChange({
                      target: { name: question.id, value: opt.value },
                    })
                  }
                  required={question.required}
                  aria-describedby={descId}
                  // enlarge the glyph; label provides the 44px target
                  style={{ transform: "scale(1.35)" }}
                  className="accent-primary-600"
                />
                <span className="text-base">
                  {t(opt.labelKey ?? opt.label)}
                </span>
              </label>
            );
          })}

          {rangeDesc && <p className="muted-text">{rangeDesc}</p>}
          {Array.isArray(question.legendList) &&
            question.legendList.length > 0 && (
              <ul className="text-xs mt-2 list-disc pl-5">
                {question.legendList.map((k) => (
                  <li key={k}>{t(k)}</li>
                ))}
              </ul>
            )}
        </fieldset>
      );
    case "boolean-matrix": {
      const checked = (rowId, bool) => (value ?? {})[rowId] === bool;
      const handle = (rowId, bool) => {
        const cur = value ?? {};
        onChange({
          target: { name: question.id, value: { ...cur, [rowId]: bool } },
        });
      };

      return (
        <div className="boolean-matrix overflow-x-auto">
          {helperText && (
            <p id={descId} className="muted-text mb-2">
              {helperText}
            </p>
          )}

          <table className="w-full text-base">
            <thead>
              <tr>
                <th className="text-left py-3 pr-2"></th>
                {question.columns.map((c, idx) => (
                  <th
                    key={String(c.value)}
                    id={`${question.id}__col_${idx}`}
                    className="py-3 px-2"
                  >
                    {t(c.labelKey ?? c.label)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {question.rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td
                    id={`${question.id}__row_${r.id}`}
                    className="py-3 pr-2 align-top"
                  >
                    {t(r.labelKey ?? r.label)}
                  </td>

                  {question.columns.map((c, idx) => {
                    const isChecked = checked(r.id, c.value);
                    return (
                      <td key={String(c.value)} className="py-1 px-1">
                        <label className="radio-cell">
                          <input
                            type="radio"
                            name={`${question.id}__${r.id}`}
                            checked={isChecked}
                            onChange={() => handle(r.id, c.value)}
                            required={question.required}
                            aria-labelledby={`${question.id}__row_${r.id} ${question.id}__col_${idx}`}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "textarea":
      return (
        <textarea
          {...common}
          rows={4}
          placeholder={t(
            question.placeholderKey ??
              question.placeholder ??
              question.titleKey ??
              question.title,
          )}
        />
      );
    default:
      // ----- text (default) -----
      return <input type="text" {...common} />;
  }
}
